import { useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useToast } from '../context/ToastContext';

type ImportType = 'marjane' | 'frs' | 'bl' | 'reception';

const ENDPOINTS: Record<ImportType, string> = {
  marjane: '/v1/deliveries/import',
  frs: '/v1/imports/frs',
  bl: '/v1/imports/bl',
  reception: '/v1/imports/reception-globale',
};

const LABELS: Record<ImportType, string> = {
  marjane: 'Décompte Marjane (clients)',
  frs: 'Suivi fournisseurs (FRS)',
  bl: 'Dépôts Marjane par N° de BL',
  reception: 'Réceptions globales chez Marjane',
};

const HINTS: Record<ImportType, string> = {
  marjane: "Colonnes attendues : date, client, et une colonne par type de caisse.",
  frs: "Colonnes attendues : DATE, CNUF, FOURNISSEUR, CAGEOT PM, CAGEOT GM. Ces données sont affichées à part pour comparaison — aucun mouvement comptable n'est créé.",
  bl: "Colonnes attendues : DATE livraison, N BL, N caisse. Alimente le rapport d'écart mensuel (quantité déclarée par BL vs mouvements réellement enregistrés).",
  reception: "Colonnes attendues : DATE, REÇU PM, REÇU GM. Devient un vrai mouvement de réception (marjane → central), sans ambiguïté métier.",
};

interface PreviewRow {
  [key: string]: unknown;
  status: string;
  error?: string;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function rowColumns(rows: PreviewRow[]): string[] {
  const keys = new Set<string>();
  for (const r of rows) for (const k of Object.keys(r)) if (k !== 'status' && k !== 'error') keys.add(k);
  return Array.from(keys);
}

function fmtCell(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

export function ImportMarjane() {
  const toast = useToast();
  const [type, setType] = useState<ImportType>('marjane');
  const [text, setText] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [rows, setRows] = useState<PreviewRow[] | null>(null);
  const [msg, setMsg] = useState<{ text: string; err: boolean } | null>(null);
  const [busy, setBusy] = useState(false);

  function changeType(t: ImportType) {
    setType(t); setRows(null); setMsg(null);
  }

  async function buildBody(commit: boolean): Promise<Record<string, unknown> | null> {
    if (file) return { file_b64: await fileToBase64(file), commit };
    if (text.trim()) return { text, commit };
    setMsg({ text: 'Collez le contenu du fichier, ou chargez un fichier .xlsx.', err: true });
    return null;
  }

  async function preview() {
    const body = await buildBody(false);
    if (!body) return;
    setBusy(true);
    try {
      const d = await api<{ preview: PreviewRow[] }>(ENDPOINTS[type], { method: 'POST', body });
      setRows(d.preview); setMsg(null);
    } catch (e) { setMsg({ text: e instanceof ApiError ? e.message : 'Erreur', err: true }); }
    finally { setBusy(false); }
  }

  async function commit() {
    const body = await buildBody(true);
    if (!body) return;
    setBusy(true);
    try {
      const d = await api<{ created?: number; already_imported?: number; to_review?: PreviewRow[] }>(ENDPOINTS[type], { method: 'POST', body });
      const parts = [];
      if (d.created !== undefined) parts.push(`${d.created} ligne(s) importée(s)`);
      if (d.already_imported) parts.push(`${d.already_imported} déjà importée(s) (ignorée(s))`);
      setMsg({ text: parts.length ? parts.join(', ') + '.' : 'Import confirmé.', err: false });
      toast('Import confirmé');
      if (d.to_review && d.to_review.length) setRows(d.to_review);
      else setRows(null);
    } catch (e) { setMsg({ text: e instanceof ApiError ? e.message : 'Erreur', err: true }); }
    finally { setBusy(false); }
  }

  const cols = rows ? rowColumns(rows) : [];

  return (
    <>
      <div className="pagehead"><h1>Import</h1></div>
      <div className="card">
        <h2>{LABELS[type]}</h2>
        <label htmlFor="importType">Type de fichier</label>
        <select id="importType" value={type} onChange={(e) => changeType(e.target.value as ImportType)}>
          {Object.entries(LABELS).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
        </select>
        <div className="muted" style={{ margin: '0.5rem 0 1rem' }}>{HINTS[type]}</div>

        <label htmlFor="importFile">Fichier Excel (.xlsx)</label>
        <input id="importFile" type="file" accept=".xlsx,.xls"
          onChange={(e) => { setFile(e.target.files?.[0] || null); setText(''); }} />

        <div className="row" style={{ margin: '0.6rem 0', alignItems: 'center', gap: '8px' }}>
          <div className="muted">— ou —</div>
        </div>

        <label htmlFor="importText">Contenu collé (CSV / texte)</label>
        <textarea id="importText" value={text}
          onChange={(e) => { setText(e.target.value); if (e.target.value) setFile(null); }}
          placeholder="Collez ici le contenu du fichier" />

        <div className="row" style={{ marginTop: '0.8rem' }}>
          <button onClick={preview} disabled={busy}>Aperçu</button>
          <button className="primary" onClick={commit} disabled={busy}>Confirmer l'import</button>
        </div>
        {msg && <div className={msg.err ? 'err' : 'ok'}>{msg.text}</div>}
      </div>

      {rows && rows.length > 0 && (
        <div className="card">
          <h2>Aperçu</h2>
          <div className="tblwrap"><table><tbody>
            <tr>{cols.map((c) => <th key={c}>{c}</th>)}<th>Statut</th></tr>
            {rows.map((r, idx) => (
              <tr key={idx}>
                {cols.map((c) => <td key={c}>{fmtCell(r[c])}</td>)}
                <td><span className={'badge ' + (r.status === 'ok' ? 'ok' : r.status === 'error' || r.status === 'invalid' ? 'crit' : 'warn')}>
                  {r.status}{r.error ? ' — ' + r.error : ''}
                </span></td>
              </tr>
            ))}
          </tbody></table></div>
        </div>
      )}
    </>
  );
}
